/**
 * Asks before a route change while typed text is still unsaved.
 * The app uses BrowserRouter, which has no navigation blocker, so
 * the doors that call navigate check here. A page may open its own
 * dialog instead of window.confirm. beforeunload covers reload.
 * Scene Writing and the note forms share this. The newest registration
 * wins, so a page can replace the message when it mounts and restore
 * the previous one when it unmounts.
 */
import { DRAFT_LEAVE_WARNING } from './writerCopy';

const guards = [];

export function registerUnsavedLeaveGuard(isBlocked, message = DRAFT_LEAVE_WARNING, ask = null) {
  const entry = {
    isBlocked: typeof isBlocked === 'function' ? isBlocked : () => false,
    message: message || DRAFT_LEAVE_WARNING,
    // A page can open its own dialog. Otherwise the browser confirm is used.
    ask: typeof ask === 'function' ? ask : null,
  };
  guards.push(entry);
  return () => {
    const index = guards.indexOf(entry);
    if (index >= 0) guards.splice(index, 1);
  };
}

export function registerDraftLeaveGuard(isBlocked) {
  return registerUnsavedLeaveGuard(isBlocked, DRAFT_LEAVE_WARNING);
}

function blockedLeaveGuard() {
  for (let i = guards.length - 1; i >= 0; i -= 1) {
    if (guards[i].isBlocked()) return guards[i];
  }
  return null;
}

/**
 * True when the writer may leave now.
 * A page dialog (ask) opens and this returns false; the caller must not
 * navigate until that dialog runs the resume it was given.
 * Without ask, the browser confirm decides.
 */
export function allowDraftLeave() {
  const guard = blockedLeaveGuard();
  if (!guard) return true;
  if (guard.ask) {
    guard.ask(null);
    return false;
  }
  return window.confirm(guard.message);
}

/**
 * Run `action` now, or after the writer discards unsaved edits.
 * Keep editing runs nothing. A browser confirm still decides when the
 * page did not register its own dialog.
 */
export function runAfterLeaveGuard(action) {
  const go = typeof action === 'function' ? action : () => {};
  const guard = blockedLeaveGuard();
  if (!guard) {
    go();
    return true;
  }
  if (guard.ask) {
    guard.ask(go);
    return false;
  }
  if (window.confirm(guard.message)) {
    go();
    return true;
  }
  return false;
}
